const { generateImageInfo } = require("./generators/ImageInfo");
const { generateMain } = require("./generators/Main");
const { generateMarkerInfo } = require("./generators/MarkerInfo");
const { generateNotesList } = require("./generators/Notes");
const PrerenderedDataKey = require("./enums/PrerenderedDataKey");
const MainKey = require("./enums/MainKey");
const MainAssessmentKey = require("./enums/MainAssessmentKey");
const Status = require("./enums/Status");
const ReasonCode = require("./enums/ReasonCode");
const ImageInfoKey = require("./enums/ImageInfoKey");
const JSZip = require("jszip");
const PrerenderedDataFilename = require("./enums/PrerenderedDataFilename");
const PrerenderedImageInfoKey = require("./enums/PrerenderedImageInfoKey");
const {
  isValidStatusAndReasonPair,
  parseStatusFromReasonCode,
} = require("./utilities/statusAndReasonCode");
const path = require("./utilities/pathFunctions");

// #region privateUpdateMethods

/**
 * Create updated marker info object
 *
 * @param {IRRenderer} renderer Initialized IRRenderer instance
 * @param {Object} infoJSON Object containing IS2 metadata (info.json)
 * @returns {Object} Updated marker info
 */
const _createMarkerInfoObject = (renderer, infoJSON) => {
  const markerInfo = generateMarkerInfo(renderer, infoJSON);
  return markerInfo;
};

/**
 * Create updated notes list
 *
 * @param {Object} infoJSON Object containing IS2 metadata (info.json)
 * @returns {Array} Updated notes list
 */
const _createNotesListObject = (infoJSON) => {
  const notesList = generateNotesList(infoJSON);
  return notesList;
};

/**
 * Create updated main object
 *
 * @param {Object} prerenderedInfo Object containing prerendered data
 * @param {Object} updatedInfo Object containing the updated info
 * @param {number} [updatedInfo.statusNumber] Updated status number (optional)
 * @param {number} [updatedInfo.reasonCodeNumber] Updated reason code number (optional)
 * @returns {Object} Updated main object
 */
const _createMainObject = (prerenderedInfo, updatedInfo) => {
  const { statusNumber = undefined, reasonCodeNumber = undefined } =
    updatedInfo;

  const main = { ...prerenderedInfo[PrerenderedDataKey.MAIN] };

  if (statusNumber !== undefined) {
    main[MainKey.ASSESSMENT][MainAssessmentKey.STATUS_NUMBER] = statusNumber;
    main[MainKey.ASSESSMENT][MainAssessmentKey.STATUS_TEXT] =
      Status.getStatusText(statusNumber);
  }

  if (reasonCodeNumber !== undefined) {
    main[MainKey.ASSESSMENT][MainAssessmentKey.REASON_CODE_NUMBER] =
      reasonCodeNumber;
    main[MainKey.ASSESSMENT][MainAssessmentKey.REASON_CODE_TEXT] =
      ReasonCode.getReasonCodeText(reasonCodeNumber);
  }

  // make sure that the reason code and status are valid
  const mainStatusNumber =
    main[MainKey.ASSESSMENT][MainAssessmentKey.STATUS_NUMBER];
  const mainReasonCodeNumber =
    main[MainKey.ASSESSMENT][MainAssessmentKey.REASON_CODE_NUMBER];

  if (!isValidStatusAndReasonPair(mainStatusNumber, mainReasonCodeNumber)) {
    throw new Error("Invalid status and reason code pair");
  }

  return main;
};

/**
 * Create updated image info object
 *
 * @param {Object} prerenderedInfo Object containing prerendered data
 * @param {Object} infoJSON Object containing IS2 metadata (info.json)
 * @returns {Array} Updated image info
 */
const _createImageInfoObject = (prerenderedInfo, infoJSON) => {
  // Revert the date taken to the original date taken
  const originalDateAndTime = prerenderedInfo[
    PrerenderedDataKey.IMAGE_INFO
  ].find(
    (info) => info[ImageInfoKey.NAME] === PrerenderedImageInfoKey.DATE_AND_TIME
  );

  if (!originalDateAndTime) {
    throw new Error(
      `Original "${PrerenderedImageInfoKey.DATE_AND_TIME}" not found in prerendered info`
    );
  }

  // Regenerate the image info so that all values
  // are updated. This is necessary because values such as
  // Background Temperature and Emissivity are configurable
  const updatedImageInfo = generateImageInfo(
    infoJSON,
    originalDateAndTime[ImageInfoKey.VALUE]
  );

  return updatedImageInfo;
};

// #endregion

/**
 * Extract from .is2 file
 * @param {IRRenderer} renderer Initialized IRRenderer instance
 * @param {ArrayBuffer} is2Buffer ArrayBuffer of the .is2 file
 * @param {Function} extractionCallback Callback function that returns the extracted data (signature: (renderer, is2Handle) => {})
 * @returns {Object} infoJSON
 */
const extractFromIS2 = (
  renderer,
  is2Buffer,
  extractionCallback = (renderer, is2Handle) => {}
) => {
  let is2Handle = null;

  try {
    is2Handle = renderer.is2_open_buffer(is2Buffer, true);
    return extractionCallback(renderer, is2Handle);
  } finally {
    if (is2Handle) {
      renderer.is2_close(is2Handle);
    }
  }
};

/**
 * Generate prerendered info object
 * @param {IRRenderer} renderer Initialized IRRenderer instance
 * @param {ArrayBuffer} is2Buffer ArrayBuffer of the .is2 file
 * @param {Object} imageIndexDatabaseInfo Image index database entry (see image_index table of the database schema)
 * @returns {Object} Prerendered info object
 */
const generatePrerenderedInfo = (
  renderer,
  is2Buffer,
  imageIndexDatabaseInfo
) => {
  const {
    image_id,
    date_taken_milliseconds,
    date_posted_milliseconds,
    date_modified_milliseconds,
    user_timezone,
    status,
    reason_code,
  } = imageIndexDatabaseInfo;

  let infoJSON = extractFromIS2(renderer, is2Buffer, (renderer, is2Handle) => {
    return renderer.is2_get_metadata(is2Handle);
  });

  const imageInfo = generateImageInfo(infoJSON, date_taken_milliseconds);
  const main = generateMain({
    image_id,
    date_taken_milliseconds,
    date_posted_milliseconds,
    date_modified_milliseconds,
    user_timezone,
    status,
    reason_code,
  });
  const markerInfo = generateMarkerInfo(renderer, infoJSON);
  const notesList = generateNotesList(infoJSON);

  return Object.freeze({
    [PrerenderedDataKey.IMAGE_INFO]: imageInfo,
    [PrerenderedDataKey.MAIN]: main,
    [PrerenderedDataKey.MARKER_INFO]: markerInfo,
    [PrerenderedDataKey.NOTES]: notesList,
  });
};

/**
 * Update marker info in prerendered info object
 * @param {Object} prerenderedInfo Object containing prerendered data
 * @param {IRRenderer} renderer Initialized IRRenderer instance
 * @param {ArrayBuffer} is2Buffer ArrayBuffer of the .is2 file
 * @returns {Object} Updated prerendered info object
 */
const updateMarkerInfo = (prerenderedInfo, renderer, is2Buffer) => {
  const infoJSON = extractFromIS2(
    renderer,
    is2Buffer,
    (renderer, is2Handle) => {
      return renderer.is2_get_metadata(is2Handle);
    }
  );
  const updatedMarkerInfo = _createMarkerInfoObject(renderer, infoJSON);
  return Object.freeze({
    ...prerenderedInfo,
    [PrerenderedDataKey.MARKER_INFO]: updatedMarkerInfo,
  });
};

/**
 * Update notes list in prerendered info object
 * @param {Object} prerenderedInfo Object containing prerendered data
 * @param {IRRenderer} renderer Initialized IRRenderer instance
 * @param {ArrayBuffer} is2Buffer ArrayBuffer of the .is2 file
 * @returns {Object} Updated prerendered info object
 */
const updateNotesList = (prerenderedInfo, renderer, is2Buffer) => {
  const infoJSON = extractFromIS2(
    renderer,
    is2Buffer,
    (renderer, is2Handle) => {
      return renderer.is2_get_metadata(is2Handle);
    }
  );

  const updatedNotesList = _createNotesListObject(infoJSON);
  return Object.freeze({
    ...prerenderedInfo,
    [PrerenderedDataKey.NOTES]: updatedNotesList,
  });
};

/**
 * Update main in prerendered info object
 * @param {Object} prerenderedInfo Object containing prerendered data
 * @param {Object} updatedInfo Object containing the updated info
 * @param {number} [updatedInfo.statusNumber] Updated status number (optional)
 * @param {number} [updatedInfo.reasonCodeNumber] Updated reason code number (optional)
 * @returns {Object} Updated prerendered info object
 */
const updateMain = (prerenderedInfo, updatedInfo) => {
  const main = _createMainObject(prerenderedInfo, updatedInfo);
  return Object.freeze({
    ...prerenderedInfo,
    [PrerenderedDataKey.MAIN]: main,
  });
};

/**
 * Update image info in prerendered info object
 * @param {Object} prerenderedInfo Object containing prerendered data
 * @param {IRRenderer} renderer Initialized IRRenderer instance
 * @param {ArrayBuffer} is2Buffer ArrayBuffer of the .is2 file
 * @returns {Object} Updated prerendered info object
 */
const updateImageInfo = (prerenderedInfo, renderer, is2Buffer) => {
  const infoJSON = extractFromIS2(
    renderer,
    is2Buffer,
    (renderer, is2Handle) => {
      return renderer.is2_get_metadata(is2Handle);
    }
  );

  const imageInfo = _createImageInfoObject(prerenderedInfo, infoJSON);
  return Object.freeze({
    ...prerenderedInfo,
    [PrerenderedDataKey.IMAGE_INFO]: imageInfo,
  });
};

/**
 * Write prerendered data to a zip file.
 *
 * @param {ArrayBuffer} is2Buffer ArrayBuffer of the .is2 file.
 * @param {Object} prerenderedInfo Object containing prerendered data.
 * @param {IRRenderer} renderer Initialized IRRenderer instance.
 * @returns {Promise<ArrayBuffer>} The generated zip file as an ArrayBuffer.
 */
const writePrerenderedData = async (is2Buffer, prerenderedInfo, renderer) => {
  // Just check to make sure that prerenderedInfo is an object
  // with the correct keys
  const requiredKeys = [
    PrerenderedDataKey.IMAGE_INFO,
    PrerenderedDataKey.MAIN,
    PrerenderedDataKey.MARKER_INFO,
    PrerenderedDataKey.NOTES,
  ];
  if (
    !requiredKeys.every((key) => Object.keys(prerenderedInfo).includes(key))
  ) {
    throw new Error("Invalid prerenderedInfo object");
  }

  const [vlImage, previewImage, photonotes] = extractFromIS2(
    renderer,
    is2Buffer,
    (renderer, is2Handle) => {
      // Read photonotes
      const photonotes = [];
      const numPhotonotes = renderer.is2_get_photonotes(is2Handle);

      for (let i = 0; i < numPhotonotes; i++) {
        const buffer = renderer.is2_get_photonote(i);
        const filepath = renderer.is2_get_photonote_path(i);

        photonotes.push({
          filepath,
          buffer,
        });
      }

      return [
        renderer.is2_get_vldata(is2Handle),
        renderer.is2_get_thumbnail(is2Handle),
        photonotes,
      ];
    }
  );

  const zip = new JSZip();
  await zip.loadAsync(is2Buffer);

  // Remove Prerendered Data Directory and do a fresh write
  // This is necessary because we want to overwrite the existing
  // files with the updated ones
  Object.keys(zip.files).forEach((filename) => {
    if (
      filename.startsWith(PrerenderedDataFilename.PRERENDERED_DATA_DIRECTORY)
    ) {
      zip.remove(filename);
    }
  });

  zip.file(PrerenderedDataFilename.INFO_JSON, JSON.stringify(prerenderedInfo));
  zip.file(PrerenderedDataFilename.VISUAL_IMAGE, vlImage);
  zip.file(PrerenderedDataFilename.PREVIEW_IMAGE, previewImage);

  // Add photonotes
  // NOTE: If photonotes are not present, the photonotes directory will not be created
  photonotes.forEach((photonoteObj) => {
    const { buffer, filepath } = photonoteObj;

    const photonotePath = path.join(
      PrerenderedDataFilename.PHOTONOTE_DIRECTORY,
      filepath
    );
    zip.file(photonotePath, buffer);
  });

  return await zip.generateAsync({ type: "arraybuffer" });
};

/**
 * Read prerendered data from IS2 file
 *
 * @param {ArrayBuffer} is2Buffer ArrayBuffer of the .is2 file.
 * @returns {Promise<Object>} Object containing the prerendered data.
 * @property {Object} prerenderedInfo Object containing the prerendered data.
 * @property {Blob} vlImage The visual image as a Blob.
 * @property {Blob} previewImage The preview image as a Blob.
 * @property {Array} photonotes An array of photonotes.
 * @property {string} photonotes[].name The name of the photonote.
 * @property {Blob} photonotes[].src The photonote as a Blob.
 */
const readPrerenderedData = async (is2Buffer) => {
  const zip = new JSZip();
  await zip.loadAsync(is2Buffer);
  const prerenderedInfoText = await zip
    .file(PrerenderedDataFilename.INFO_JSON)
    .async("text");
  const prerenderedInfo = JSON.parse(prerenderedInfoText);
  const vlImage = await zip
    .file(PrerenderedDataFilename.VISUAL_IMAGE)
    .async("blob");
  const previewImage = await zip
    .file(PrerenderedDataFilename.PREVIEW_IMAGE)
    .async("blob");

  const photonoteFilenames = Object.keys(zip.files).filter((filename) => {
    return (
      filename.startsWith(PrerenderedDataFilename.PHOTONOTE_DIRECTORY) &&
      !filename.endsWith("/") // Exclude directories
    );
  });

  const photonotes = await Promise.all(
    photonoteFilenames.map(async (filename) => {
      const contentBuffer = await zip.file(filename).async("blob");
      // Remove Prerendered directory from name
      const name = filename.replace(
        PrerenderedDataFilename.PHOTONOTE_DIRECTORY + "/",
        ""
      );
      return {
        name: name,
        src: contentBuffer,
      };
    })
  );

  return {
    prerenderedInfo,
    vlImage,
    previewImage,
    photonotes,
  };
};

/**
 * Update prerendered data
 *
 * NOTE: If status and reason code are not provided, the function will
 *       attempt to extract them from the .is2 file
 *
 * @param {ArrayBuffer} is2Buffer ArrayBuffer of the .is2 file
 * @param {IRRenderer} renderer Initialized IRRenderer instance
 * @param {number} [status] Updated status number (optional)
 * @param {number} [reasonCode] Updated reason code number (optional)
 * @returns {Promise<ArrayBuffer>} The updated prerendered data as an ArrayBuffer
 */
const updatePrerenderedData = async (
  is2Buffer,
  renderer,
  status = undefined,
  reasonCode = undefined
) => {
  const { prerenderedInfo } = await readPrerenderedData(is2Buffer);
  const infoJSON = extractFromIS2(
    renderer,
    is2Buffer,
    (renderer, is2Handle) => {
      const metadata = renderer.is2_get_metadata(is2Handle);
      return metadata;
    }
  );

  if (status === undefined && reasonCode === undefined) {
    // IRViewer key in the IS2 metadata contains reason code
    // From there, we can decipher the status
    const irviewerReasonCode = infoJSON?.IRVIEWER?.status?.code;
    if (irviewerReasonCode || irviewerReasonCode === 0) {
      const statusCode = parseStatusFromReasonCode(irviewerReasonCode);
      status = statusCode;
      reasonCode = irviewerReasonCode;
    }
  }

  // Perform updates
  let updatedMain = _createMainObject(prerenderedInfo, {
    statusNumber: status,
    reasonCodeNumber: reasonCode,
  });
  updatedMain = {
    ...updatedMain,
    date_modified_milliseconds: Date.now(),
  };

  const updatedMarkerInfo = _createMarkerInfoObject(renderer, infoJSON);
  const updatedNotesList = _createNotesListObject(infoJSON);
  const updatedImageInfo = _createImageInfoObject(prerenderedInfo, infoJSON);

  const updatedPrerenderedInfo = {
    [PrerenderedDataKey.MAIN]: updatedMain,
    [PrerenderedDataKey.MARKER_INFO]: updatedMarkerInfo,
    [PrerenderedDataKey.NOTES]: updatedNotesList,
    [PrerenderedDataKey.IMAGE_INFO]: updatedImageInfo,
  };

  // Write updated prerendered data to a zip file
  return writePrerenderedData(is2Buffer, updatedPrerenderedInfo, renderer);
};

module.exports = {
  generatePrerenderedInfo,
  updateMarkerInfo,
  updateNotesList,
  updateMain,
  updateImageInfo,
  writePrerenderedData,
  readPrerenderedData,
  updatePrerenderedData,
};
